/**
 * Les deux garde-fous de la navigation pendant une rédaction (#859).
 *
 * Ne rend rien. Monté **une seule fois par la coque**, et c'est le sujet : le
 * texte d'une rédaction ne passe par aucun tampon serveur (#857), donc ce qui
 * doit le protéger ne peut pas vivre dans un écran qui se démonte quand
 * l'avocat s'en va — ce serait précisément l'instant où la protection disparaît.
 *
 * Deux choses de nature différente, réunies ici parce qu'elles répondent toutes
 * deux à « l'avocat s'en va » :
 * - **le message pédagogique** au premier départ d'un fil qui rédige — il
 *   n'empêche rien, il apprend ;
 * - **l'avertissement natif du navigateur** à la fermeture de l'onglet — lui
 *   seul peut empêcher une perte réelle.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useGlobalConversationList } from '@/features/hector-agent-casediscovery-v3/hooks/useGlobalConversationList';
import { useConversationAffichee } from '@/features/hector-agent-casediscovery-v3/state/conversationAffichee';
import { useConversationsQuiRedigent } from '../streaming/useConversationsQuiRedigent';
import { nomAffichableDeConversation } from '../state/canvasHolder';
import { deciderAvertissementDeDepart, messageDeDepart } from '../state/redactionDepart';
import { canvasDocumentRuntime } from '../state/canvasDocumentRuntime';

/**
 * **Exactement la projection de la barre latérale**, comme la pastille du
 * porteur (#853) : la clé TanStack inclut la projection d'archive, donc une
 * autre valeur créerait une seconde requête sur chaque page.
 */
const PROJECTION_ARCHIVE = { archived: 'include' } as const;

export function RedactionNavigationGuards(): null {
  const quiRedigent = useConversationsQuiRedigent();
  const conversationAffichee = useConversationAffichee();
  const { conversations, selectConversation } = useGlobalConversationList(PROJECTION_ARCHIVE);

  // ---- Le message pédagogique, une fois par onglet -------------------------
  const precedenteRef = React.useRef<string | null>(null);
  const dejaAvertiRef = React.useRef(false);
  // Les noms changent (renommage, arrivée de la liste) sans constituer un
  // départ : les lire par référence évite de rejouer l'effet pour autant.
  const conversationsRef = React.useRef(conversations);
  conversationsRef.current = conversations;
  const quiRedigentRef = React.useRef(quiRedigent);
  quiRedigentRef.current = quiRedigent;
  const selectConversationRef = React.useRef(selectConversation);
  selectConversationRef.current = selectConversation;

  React.useEffect(() => {
    const quitte = deciderAvertissementDeDepart({
      precedente: precedenteRef.current,
      courante: conversationAffichee,
      quiRedigent: quiRedigentRef.current,
      dejaAverti: dejaAvertiRef.current,
    });
    precedenteRef.current = conversationAffichee;
    if (!quitte) return;

    dejaAvertiRef.current = true;
    const nom = nomAffichableDeConversation(
      conversationsRef.current.find((c) => c.id === quitte)?.title,
    );
    toast.info(messageDeDepart(nom), {
      // L'avocat vient de partir vers autre chose : lui rendre le chemin du
      // retour évite que « la rédaction continue là-bas » soit une information
      // sans suite.
      action: { label: 'Y retourner', onClick: () => selectConversationRef.current(quitte) },
    });
    // `conversationAffichee` SEUL en dépendance : c'est le départ qu'on guette.
    // Ajouter la liste des rédactions ferait rejouer l'effet quand une
    // rédaction démarre ou s'arrête, sans qu'aucune navigation ait eu lieu — et
    // le comparateur, lui, ne verrait pas la différence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationAffichee]);

  // ---- L'avertissement natif de fermeture d'onglet -------------------------
  const uneRedactionTourne = quiRedigent.length > 0;
  React.useEffect(() => {
    const auDepart = (evenement: BeforeUnloadEvent): void => {
      const brouillonManuelNonSauvegarde = canvasDocumentRuntime.getSessionsSnapshot().sessions.some((session) => {
        const snapshot = canvasDocumentRuntime.getDraftController(session.conversationId)?.getSnapshot();
        return snapshot !== null && snapshot !== undefined && snapshot.savedVersion < snapshot.version;
      });
      if (!uneRedactionTourne && !brouillonManuelNonSauvegarde) return;
      // Les navigateurs imposent leur propre texte depuis 2021 : `preventDefault`
      // est la seule chose qui compte, toute chaîne serait ignorée. C'est aussi
      // pourquoi le message pédagogique ci-dessus existe — c'est le seul endroit
      // où l'on peut dire à l'avocat *pourquoi* on le retient.
      evenement.preventDefault();
      // Certains moteurs anciens n'arment la boîte que si `returnValue` est
      // renseigné ; la valeur elle-même n'est jamais affichée.
      evenement.returnValue = '';
    };
    window.addEventListener('beforeunload', auDepart);
    return () => window.removeEventListener('beforeunload', auDepart);
  }, [uneRedactionTourne]);

  return null;
}
